import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "../../../atoms/button";
import Typography from "../../../atoms/typography";
import IconButtonComponent from "../../../atoms/iconButton";
import Drawer from "../../drawer";
import { constants } from "../../../../constants/pageConstants/pdpConstants";
import {
  requestToCustomizableContent,
  setCustomizedImageContent,
  setUserPersonalization,
} from "../../../../store/slices/personalizationSlice";
import {
  handlePersonalizationDetailsOnRefresh,
  handleSetUserPersonalizations,
} from "../../../../utils/pdp/personalization";
import { unlockBody } from "../../../../utils/document";
import HTMLReactParser from "html-react-parser";
import Loader from "../../../atoms/loader";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import useAnalytics from "../../../../analytics/useAnalytics";
import { COUNTRIES } from "../../../../constants/common/common";

/**
 * CustomizedImagePersonalization component allows users to upload and customize images
 * for a personalized product. It supports both mobile and desktop views, handling the
 * image upload process through a drawer interface.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.isMobile - Determines if the component is rendered in mobile view.
 * @param {object} props.item - The item data containing fieldName and contentType.
 * @param {string} props.productId - The product ID for the item being personalized.
 * @param {string} props.urlIdentifier - URL identifier for fetching custom content.
 * @param {Function} props.setPreviewImage - Function to set the preview image URL.
 * @param {string} props.previewImage - The URL of the current preview image.
 * @returns {React.ReactElement} The CustomizedImagePersonalization component.
 */
function CustomizedImagePersonalization({
  isMobile,
  item,
  productId,
  urlIdentifier,
  setPreviewImage,
  previewImage,
  cleverTapData,
}) {
  const { fieldName, contentType, label } = item;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const selectedPincode = useSelector((state) => state.locationLock.selectedDomesticLocation?.pincode);
  const personalizedContentHtml = useSelector((state) => state.personalization.customizedImageContent);
  const userPersonalizationData = useSelector((state) => state.personalization.userPersonalization);
  const catalogId = useSelector((state) => state?.appConfig?.catalogId);
  const selectedIntLocation = useSelector((state) => state?.locationLock.selectedIntLocation);

  const clearPersonalizedData = useSelector((state) => state.personalization.clearPersonalizedData);

  useEffect(() => {
    setPreviewImage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPersonalizedData]);

  useEffect(() => {
    // Fetch data from local storage on component load
    const personalizationFromLocalStore = handlePersonalizationDetailsOnRefresh(productId);
    if (personalizationFromLocalStore && Array.isArray(personalizationFromLocalStore)) {
      dispatch(setUserPersonalization(personalizationFromLocalStore));
      const matchingEntry = personalizationFromLocalStore?.find((entry) => entry.fieldName === fieldName);

      if (matchingEntry) {
        if (matchingEntry.previewImage) {
          setPreviewImage(matchingEntry.previewImage);
        } else if (matchingEntry.value?.[0]) {
          setPreviewImage(matchingEntry.value[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, productId]);

  /**
   * This function loads the provided url as a script
   *
   * @param {string} url is the url that has to be loaded.
   * @returns {void} returns nothing.
   */
  const loadScript = (url) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    if (script.readyState) {
      script.onreadystatechange = () => {
        if (script.readyState === "loaded" || script.readyState === "complete") {
          script.onreadystatechange = null;
        }
      };
    }
    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
  };

  /**
   * Fetches customizable content and opens the drawer.
   */
  const onHandleUploadPhotoHandler = () => {
    setIsDrawerOpen(true);
    dispatch(requestToCustomizableContent({ urlIdentifier, productId }));
  };

  /**
   * Closes the drawer and cleans up any open elements.
   */
  const onHandleDrawerClose = () => {
    unlockBody();
    setIsDrawerOpen(false);
    dispatch(setCustomizedImageContent(null));
  };

  /**
   * Handles closing the image drawer, updating the preview image, and setting personalization data.
   * @param {string} imagePath - The path of the image.
   * @param {string} bucket - The S3 bucket name.
   */
  function closeSingleImagePopup(imagePath, bucket, userMsg, fontfamily) {
    unlockBody();
    if (bucket && imagePath) {
      setIsDrawerOpen(false);
      const s3FilePath = `https://${bucket}.s3-accelerate.amazonaws.com/pz/${imagePath}.jpeg`;
      setPreviewImage(s3FilePath);
      trackEvent(
        cleverTapCommonEvents.uploadPhotoDone,
        {
          ...cleverTapData,
          photo_count: 1,
          is_photo_uploaded: true,
        },
        ["clevertap"],
      );
      const personalizationValue = {
        fieldName,
        contentType,
        label,
        value: [s3FilePath],
      };

      // Add userMsg and fontfamily if they have values
      if (userMsg && fontfamily) {
        personalizationValue.value = [userMsg];
        personalizationValue.font = fontfamily;
        personalizationValue.previewImage = s3FilePath;
      }

      handleSetUserPersonalizations(productId, personalizationValue, userPersonalizationData, dispatch);
      dispatch(setCustomizedImageContent(null));
    }
  }

  useEffect(() => {
    if (personalizedContentHtml) {
      const htmlNodes = HTMLReactParser(personalizedContentHtml, { trim: true });
      const scriptNodes = htmlNodes.filter((node) => node.type === "script");
      scriptNodes.forEach((element) => {
        loadScript(element.props.src);
      });
      document.getElementById("imagePersonalization").innerHTML = personalizedContentHtml;
    }
  }, [personalizedContentHtml]);

  useEffect(() => {
    if (isDrawerOpen) {
      // Make global functions accessible
      window.closeSingleImagePopup = closeSingleImagePopup;

      // Cleanup function: remove the script when component unmounts or drawer closes
      return () => {
        window.closeSingleImagePopup = null; // Clean up global function
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  const typographyProps = {
    Tag: "p",
    classes: "w-full flex items-center flex-shrink-0 font-600 text-16 text-fnp-500",
  };

  const mobileDrawerHeaderClasses = "px-[16px] py-[10px] sm:flex border-b border-gray-300";
  const desktopDrawerHeaderClasses =
    "h-[48px] px-[16px] py-[10px] justify-between border-b border-gray-200 bg-white shadow-sm";

  return (
    <>
      <head>
        <script src="/assets/min/jquery-lib-bb.min.js" defer={true} />
        <script src="/assets/js/custom/three.min.js" defer={true} />
      </head>

      <ButtonComponent
        classes="text-base flex justify-center w-full"
        buttonVariant="customOutlined"
        label={
          contentType === constants.TEXT
            ? constants.CUSTOMIZE_PREVIEW
            : previewImage
              ? constants.CHANGE_PHOTO_AND_CUSTOMIZE
              : constants.UPLOAD_PHOTO_AND_CUSTOMIZE
        }
        onClick={onHandleUploadPhotoHandler}
        isRemoveExtraPadding={true}
        id="customized_image_btn"
      />

      <Drawer
        position={isMobile ? "bottom" : "right"}
        isOpen={(COUNTRIES.INDIA === catalogId ? selectedPincode : selectedIntLocation?.pincode) && isDrawerOpen}
        onDrawerClose={onHandleDrawerClose}
        styles={"p-0 h-full md:min-w-[360px] max-w-[768px] xll:max-w-[532px] xl:max-w-[400px]"}
        isDrawerCustomStyles={!isMobile}
      >
        {isMobile ? (
          <div className={`flex items-center justify-start ${mobileDrawerHeaderClasses}`}>
            <IconButtonComponent
              labelColor="w-full font-normal"
              classes="py-2"
              iconSrc="/icons/left-arrow.svg"
              onClick={onHandleDrawerClose}
            />
            <Typography {...typographyProps} classes={`${typographyProps.classes} pl-12`}>
              {constants.UPLOAD_PERSONALIZE_IMAGE}
            </Typography>
          </div>
        ) : (
          <Typography {...typographyProps} classes={`${typographyProps.classes} ${desktopDrawerHeaderClasses}`}>
            {constants.UPLOAD_PERSONALIZE_IMAGE}
          </Typography>
        )}
        {personalizedContentHtml ? <div id="imagePersonalization" /> : <Loader />}
      </Drawer>
    </>
  );
}

CustomizedImagePersonalization.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  productId: PropTypes.string.isRequired,
  urlIdentifier: PropTypes.string.isRequired,
  setPreviewImage: PropTypes.func.isRequired,
  previewImage: PropTypes.string,
  cleverTapData: PropTypes.object,
};

export default CustomizedImagePersonalization;
